import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { Feature, Tier, TierFeatureUpdate } from '../../types';
import { TIERS, TIER_LABELS } from '../../utils/tiers';
import { useFeedback } from '../../hooks/useFeedback';
import InlineFeedback from '../InlineFeedback';
import ConfirmDialog from '../ConfirmDialog';
import { TableCard, TableHeader, TableViewport, thClass, tdClass } from '../table/TableKit';

const savedInstances = (count: number) => `${count} saved ${count === 1 ? 'instance' : 'instances'}`;

function TierFeaturesTab({ features, active = true }: { features: Feature[]; active?: boolean }) {
  const queryClient = useQueryClient();
  const feedback = useFeedback();
  const [changes, setChanges] = useState<Map<string, TierFeatureUpdate>>(new Map());
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const { data: tiers, isLoading, error } = useQuery({
    queryKey: ['admin', 'tierFeatures'],
    queryFn: apiService.getTierFeatures,
    enabled: active,
    retry: false,
  });

  const toggle = (tier: Tier, featureKey: string, enabled: boolean, original: boolean) => {
    setChanges((previous) => {
      const next = new Map(previous);
      const key = `${tier}#${featureKey}`;
      if (enabled === original) next.delete(key);
      else next.set(key, { tier, feature_key: featureKey, is_enabled: enabled });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await apiService.updateTierFeatures(Array.from(changes.values()));
      setChanges(new Map());
      setConfirm(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'tierFeatures'] }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'instanceFeatures'] }),
      ]);
      feedback.showSuccess(`Updated ${savedInstances(result.updated_instances)}.`);
    } catch {
      setConfirm(false);
      feedback.showError('Failed to save tier features. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <InlineFeedback feedback={{ kind: 'error', message: 'Failed to load tier features.' }} />;
  if (isLoading || !tiers) return active ? <div role="status">Loading tier features...</div> : null;
  const affected = tiers.filter((tier) => Array.from(changes.values()).some((change) => change.tier === tier.tier));

  return (
    <div>
      <InlineFeedback feedback={feedback.feedback} className="mb-4" />
      {changes.size > 0 && (
        <div className="mb-6 flex gap-4">
          <button className="btn-secondary" disabled={saving} onClick={() => setChanges(new Map())}>Cancel tier changes</button>
          <button className="btn-primary" disabled={saving} onClick={() => setConfirm(true)}>Save tier changes</button>
        </div>
      )}
      <TableCard>
        <TableHeader title="Tier Features">
          <p className="text-sm text-secondary">Default features per tier. A change applies to every saved instance on that tier.</p>
        </TableHeader>
        <TableViewport>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={thClass}>Feature Key</th>
                <th className={thClass}>Description</th>
                {TIERS.map((tier) => {
                  const count = tiers.find((item) => item.tier === tier)?.instance_count ?? 0;
                  return (
                    <th key={tier} className={thClass}>
                      {TIER_LABELS[tier]}
                      {tier === 'free' && <div className="text-xs font-normal normal-case text-secondary">Pinned off</div>}
                      <div className="text-xs font-normal normal-case text-secondary">{savedInstances(count)}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {features.map((feature) => (
                <tr key={feature.feature_key}>
                  <td className={tdClass}>
                    <span className="px-3 py-1 text-sm font-medium bg-body-bg rounded">{feature.feature_key}</span>
                  </td>
                  <td className={`${tdClass} whitespace-normal`}>{feature.description}</td>
                  {TIERS.map((tier) => {
                    const original = tiers.find((item) => item.tier === tier)?.features[feature.feature_key] ?? false;
                    const change = changes.get(`${tier}#${feature.feature_key}`);
                    return (
                      <td key={tier} className={`${tdClass} ${change ? 'bg-yellow-900/20' : ''}`}>
                        <label className="inline-flex items-center cursor-pointer">
                          <input type="checkbox" aria-label={`${TIER_LABELS[tier]} ${feature.feature_key}`} className="sr-only peer" disabled={saving || tier === 'free'} checked={change?.is_enabled ?? original} onChange={(e) => toggle(tier, feature.feature_key, e.target.checked, original)} />
                          <span className="relative w-11 h-6 bg-gray-600 rounded-full peer-focus:ring-2 peer-focus:ring-primary peer-checked:bg-primary peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </TableViewport>
      </TableCard>
      <ConfirmDialog open={confirm} title="Update tier features" confirmLabel="Update features" pending={saving} onCancel={() => setConfirm(false)} onConfirm={save} message={affected.map((tier) => <span className="block" key={tier.tier}>This updates {savedInstances(tier.instance_count)} on {TIER_LABELS[tier.tier]}.</span>)} />
    </div>
  );
}

export default TierFeaturesTab;
